/** @prettier */

import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Chevron from '@surfline/quiver-react/dist/Chevron';
import { useHover } from '../../hooks/useHover';
import createAccessibleOnClick, { BTN } from '../../utils/createAccessibleOnClick';

const menuOptionClass = selected =>
  classNames({
    'sl-hover-menu__menu_item': true,
    'sl-hover-menu__menu_item--selected': selected,
  });

const getClass = (isHovering, useSelectOnMobile) =>
  classNames({
    'sl-hover-menu': true,
    'sl-hover-menu--hover': isHovering,
    'sl-hover-menu--mobile-select': useSelectOnMobile,
  });

const HoverMenu = ({
  onClick,
  Icon,
  onClickMenuItem,
  menuTitle,
  menuOptions,
  label,
  disabled,
  useSelectOnMobile,
  selectDescription,
  defaultValue,
}) => {
  const { hoverProps, isHovering } = useHover({
    className: '',
    bubble: false,
  });

  const handleSelect = event => {
    const { value } = event.target;
    const option = menuOptions.find(({ name }) => name === value);
    onClickMenuItem(option.value);
  };

  return (
    <div
      type="button"
      className={getClass(isHovering, useSelectOnMobile)}
      aria-haspopup={!disabled}
      aria-expanded={disabled ? false : isHovering}
      disabled={disabled}
      {...hoverProps}
      {...(!!onClick && { ...createAccessibleOnClick(onClick, BTN, false) })}
    >
      <div className="sl-hover-menu-target">
        {label && <h6 className="sl-hover-menu-label">{label}</h6>}
        {Icon ? (
          <div className="sl-hover-menu-icon">
            <Icon isHovering={isHovering} />
          </div>
        ) : (
          <Chevron direction="down" />
        )}
        {useSelectOnMobile && (
          <select
            className="sl-hover-menu-select"
            id={`cam-angle_${label}`}
            onChange={handleSelect}
            value={defaultValue}
          >
            {selectDescription && <option disabled>{selectDescription}</option>}
            {menuOptions.map(({ selected, name }) => (
              <option value={name} key={name} disabled={selected}>
                {name}
              </option>
            ))}
          </select>
        )}
      </div>
      <ul role="menu" className="sl-hover-menu__menu">
        {menuTitle && <h1 className="sl-hover-menu__menu_title">{menuTitle}</h1>}
        {menuOptions.map(({ name, selected, value }) => (
          <li key={name} className="sl-hover-menu__menu_item-container">
            <button
              type="button"
              role="menuitem"
              className={menuOptionClass(selected)}
              disabled={!!selected}
              {...createAccessibleOnClick(() => onClickMenuItem(value), BTN, false)}
            >
              {name}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

HoverMenu.propTypes = {
  onClick: PropTypes.func,
  Icon: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  onClickMenuItem: PropTypes.func.isRequired,
  menuTitle: PropTypes.string,
  menuOptions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
      selected: PropTypes.bool,
    }).isRequired
  ).isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  useSelectOnMobile: PropTypes.bool,
  defaultValue: PropTypes.string,
  selectDescription: PropTypes.string,
};

HoverMenu.defaultProps = {
  onClick: null,
  menuTitle: null,
  Icon: null,
  label: '',
  disabled: false,
  useSelectOnMobile: false,
  defaultValue: '',
  selectDescription: '',
};

export default HoverMenu;
